import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import {
  Card,
  FlatActionList,
  FlatActionListItem,
  Page,
  Stack,
  Text,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { StockCountActionsRootScreenParamList } from "../../../../types";
import {
  useCurrentCountContext,
  useStockCountContext,
  useStockCountSearchContext,
  useStockCountSyncContext,
} from "../../../../utilities/stock-count/hooks";
import { useDownloadStockCount } from "../../../../utilities/stock-count/use-download-stock-count";
import {
  StockCount,
  StockCountSource,
} from "@towersystems/roam-common/lib/generated-types";
import { useMutation } from "@apollo/client";
import { COMPLETE_STOCK_COUNT } from "../../../../utilities/stock-count";
import React from "react";
import { useTranslation } from "../../../../utilities/i18n";
import { StackScreenProps } from "@react-navigation/stack";

const textButtonProps = {
  fontWeight: "bold",
  color: "action",
  variant: "body",
} as const;

export type StockCountActionsScreenProps = StackScreenProps<
  StockCountActionsRootScreenParamList,
  "StockCountActionsScreen"
>;

export function StockCountActionsScreen({
  navigation,
  route,
}: StockCountActionsScreenProps) {
  const { t } = useTranslation("stock-counts");
  const { handleExportStockCount } = useStockCountContext();
  const { syncRemoteStockCountLines } = useStockCountSyncContext();
  const { downloadStockCount } = useDownloadStockCount();
  const { showMessage } = useFlashMessages();
  const { stockCount } = useCurrentCountContext();
  const { scanMode } = useStockCountSearchContext();
  const flashMessagesContext = useFlashMessages();

  const [m] = useMutation<{ completeStockCount: StockCount }>(
    COMPLETE_STOCK_COUNT
  );

  const { stockCountId } = route.params;

  const handleOnCompleteStockCountPress = React.useCallback(async () => {
    return m({ variables: { stockCountId: stockCountId } })
      .then((result) => {
        if (result.data?.completeStockCount) {
          /* @ts-ignore */
          navigation.reset({
            index: 0,
            routes: [
              /* @ts-ignore */
              { name: "StockCountsScreen" },
              {
                /* @ts-ignore */
                name: "StockCountCompletedRootScreen",
                params: {
                  stockCountId: result.data.completeStockCount.id,
                },
              },
            ],
          });
        }
      })
      .catch((err) => {
        flashMessagesContext.showMessage({
          message: err.message ?? t("common:error"),
          severity: "error",
        });
      });
  }, [stockCountId]);

  const items: FlatActionListItem[] = [
    {
      key: "export_all",
      title: "Export All",
      onPress: handleExportAll,
      disabled: stockCount.source !== StockCountSource.RETAILER,
    },
    {
      key: "download_csv",
      title: "Download CSV",
      onPress: handleDownloadStockCount,
    },
    {
      key: "refresh",
      title: "Refresh",
      onPress: handleRefresh,
    },
    {
      key: "scan_mode",
      title: "Scan Mode",
      subTitle: "Select which scan mode to use",
      onPress: onSelectScanModePress,
      componentRight: () => <Text {...textButtonProps}>{scanMode}</Text>,
    },
  ];

  if (stockCount.source === StockCountSource.INTERNAL) {
    items.push({
      key: "complete_count",
      title: "Complete count",
      subTitle: "Complete the stock count, cannot be undone",
      isAction: true,
      onPress: handleOnCompleteStockCountPress,
    });
  }

  async function handleDownloadStockCount() {
    return downloadStockCount(stockCountId)
      .then(() => navigation.goBack())
      .catch((e) => {
        console.log(e);
        showMessage({
          severity: "error",
          message: "Failed to download stock count",
        });
      });
  }

  async function handleExportAll() {
    return handleExportStockCount(stockCountId)
      .then(() => {
        showMessage({
          severity: "success",
          message: "Stock count items queued for export successfully",
        });
        navigation.goBack();
      })
      .catch((e) => {
        console.log(e);
        showMessage({
          severity: "error",
          message: "Failed to export stock count items",
        });
      });
  }

  async function handleRefresh() {
    navigation.goBack();
    return syncRemoteStockCountLines(stockCountId).catch((e) => {
      console.log(e);
      showMessage({
        severity: "error",
        message: "Failed to sync stock count lines",
      });
    });
  }

  function onSelectScanModePress() {
    navigation.navigate("ScanModeSelectScreen");
  }

  return (
    <Page>
      <Stack vertical>
        <Card>
          <FlatActionList items={[...items]} />
        </Card>
      </Stack>
    </Page>
  );
}
